<script setup lang="ts">
/**
 * The notification centre: callbacks due or late, new badges and places,
 * long pauses and targets at risk for supervisors. Each account sees only its
 * own. In the top bar and on the workstation. The server sends each as a
 * code and its values, written here in the user's language (review B46).
 */
import { computed, onBeforeUnmount, ref, watch } from 'vue'
import { useMutation, useQuery, useQueryClient } from '@tanstack/vue-query'
import { useRoute, useRouter } from 'vue-router'
import { useI18n } from 'vue-i18n'
import { DropdownMenuContent, DropdownMenuItem, DropdownMenuPortal, DropdownMenuRoot, DropdownMenuTrigger } from 'reka-ui'
import { api } from '@/lib/api'
import { useFormat } from '@/lib/format'
import Button from '@/ui/Button.vue'
import Icon from '@/ui/Icon.vue'
import type { IconName } from '@/ui/icons'
import type { SaidParams } from '@/lib/said'
import { useNotices } from './notices'

export interface AppNotification {
  id: string; kind: 'callback_due' | 'callback_late' | 'badge' | 'rank' | 'long_pause' | 'target_risk' | 'long_acw' | 'sim_paused'
    | 'contact_review' | 'webhook_off' | 'box_refusing'
  /** What it says, as a code and its values; the French title and text for the rows written before. */
  code: string | null; params: SaidParams | null
  title: string; body: string | null; link: string | null; createdAt: string; readAt: string | null
}
interface Answer { items: AppNotification[]; unread: number }

const ICON: Record<AppNotification['kind'], IconName> = {
  callback_due: 'clock', callback_late: 'alert-circle', badge: 'star', rank: 'trending-up',
  long_pause: 'pause', target_risk: 'trending-down', long_acw: 'clock', sim_paused: 'phone-off', contact_review: 'user', webhook_off: 'send', box_refusing: 'phone-off',
}
const TONE: Record<AppNotification['kind'], string> = {
  callback_due: 'bg-info-soft text-info-soft-fg', callback_late: 'bg-danger-soft text-danger-soft-fg',
  badge: 'bg-warning-soft text-warning-soft-fg', rank: 'bg-success-soft text-success-soft-fg',
  long_pause: 'bg-warning-soft text-warning-soft-fg', target_risk: 'bg-danger-soft text-danger-soft-fg',
  long_acw: 'bg-warning-soft text-warning-soft-fg', sim_paused: 'bg-danger-soft text-danger-soft-fg',
  contact_review: 'bg-info-soft text-info-soft-fg', webhook_off: 'bg-danger-soft text-danger-soft-fg',
  box_refusing: 'bg-danger-soft text-danger-soft-fg',
}

const { t } = useI18n()
const f = useFormat()
const notice = useNotices()
const qc = useQueryClient()
const router = useRouter()
const route = useRoute()
const q = useQuery({ queryKey: ['notifications'], queryFn: () => api<Answer>('/notifications'), refetchInterval: 30_000 })
const put = (r: Answer) => qc.setQueryData(['notifications'], r)
const read = useMutation({ mutationFn: (id: string) => api<Answer>(`/notifications/${id}/read`, { method: 'POST' }), onSuccess: put })
const readAll = useMutation({ mutationFn: () => api<Answer>('/notifications/read-all', { method: 'POST' }), onSuccess: put })

const unread = computed(() => q.data.value?.unread ?? 0)
/* A new notification: the bell rings once (not when one is read). */
const ringing = ref(false)
let rung: ReturnType<typeof setTimeout> | undefined
watch(unread, (now, was) => {
  if (was === undefined || now <= was) return
  ringing.value = false
  clearTimeout(rung)
  requestAnimationFrame(() => { ringing.value = true; rung = setTimeout(() => { ringing.value = false }, 700) })
})
onBeforeUnmount(() => clearTimeout(rung))
const title = computed(() => (unread.value ? t('gamification.notifications.unread', { count: unread.value }, unread.value) : t('gamification.notifications.title')))
function select(x: AppNotification) {
  if (!x.readAt) read.mutate(x.id)
  if (x.link && x.link !== route.path) void router.push(x.link)
}
</script>

<template>
  <DropdownMenuRoot>
    <DropdownMenuTrigger as-child>
      <Button variant="icon" icon-start="bell" :aria-label="title" :title="title" :class="ringing && 'animate-wiggle'">
        <span v-if="unread > 0" :key="unread" aria-hidden="true"
              class="tabular absolute end-1 top-1 grid h-4 min-w-4 animate-pop-in place-items-center rounded-pill bg-danger px-1 text-[10px] font-bold text-white">
          {{ unread > 99 ? '99+' : unread }}
        </span>
      </Button>
    </DropdownMenuTrigger>
    <DropdownMenuPortal>
      <DropdownMenuContent align="end" :side-offset="4" :collision-padding="8" :aria-label="t('gamification.notifications.title')"
                           class="z-dropdown max-h-[70vh] w-[360px] overflow-auto rounded-card border border-border bg-popover p-1 text-popover-foreground shadow-pop
                                  data-[state=open]:animate-in data-[state=open]:fade-in-0 data-[state=open]:zoom-in-[.97] data-[side=bottom]:slide-in-from-top-1 data-[side=top]:slide-in-from-bottom-1 data-[side=left]:slide-in-from-right-1 data-[side=right]:slide-in-from-left-1 data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=closed]:zoom-out-[.97]">
        <div class="flex items-center justify-between gap-2 px-2 py-1.5">
          <b class="text-[13px]">{{ t('gamification.notifications.title') }}</b>
          <button v-if="unread > 0" type="button" class="text-[12px] text-primary hover:underline" @click.prevent="readAll.mutate()">
            {{ t('gamification.notifications.readAll') }}
          </button>
        </div>
        <p v-if="!(q.data.value?.items ?? []).length" class="px-2 py-3 text-[13px] text-muted-foreground">{{ t('gamification.notifications.empty') }}</p>
        <DropdownMenuItem v-for="x in q.data.value?.items ?? []" :key="x.id" @select="select(x)"
                          :class="['flex cursor-pointer items-start gap-2.5 rounded-[5px] px-2 py-2 text-[13px] outline-none data-[highlighted]:bg-muted', !x.readAt && 'bg-primary/5']">
          <span :class="[TONE[x.kind], 'grid h-7 w-7 flex-none place-items-center rounded-pill']"><Icon :name="ICON[x.kind]" :size="15" /></span>
          <span class="grid min-w-0 flex-1">
            <b class="font-medium">{{ notice.title(x) }}</b>
            <span v-if="notice.body(x)" class="text-[12px] text-muted-foreground">{{ notice.body(x) }}</span>
            <small class="text-[11px] text-muted-foreground">{{ f.relative(x.createdAt) }}</small>
          </span>
          <span v-if="!x.readAt" class="mt-1.5 h-2 w-2 flex-none rounded-pill bg-primary" role="img" :aria-label="t('gamification.notifications.unreadDot')" />
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenuPortal>
  </DropdownMenuRoot>
</template>
